import { initData } from "./telegram";

// In production the frontend is served from the same origin as /api/*.
// In dev, vite proxies /api/* to the FastAPI uvicorn on :8765.
const BASE = "";
const REQUEST_TIMEOUT_MS = 30_000;

// Dev-mode escape hatch: when opened in a plain browser at
// http://localhost:5173/?u=<telegram_id>, save that id (and stick it in
// localStorage so it survives reloads). The backend must have DEV_AUTH=true
// in its .env to honour the X-Dev-User-Id header instead of HMAC. This has
// zero effect in prod: prod has DEV_AUTH unset, and the URL never carries
// `u=` anyway.
function devUserId(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("u");
    if (fromUrl) {
      localStorage.setItem("energo.dev_user_id", fromUrl);
      return fromUrl;
    }
    return localStorage.getItem("energo.dev_user_id");
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

function describeDetail(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first === "object" && "msg" in first) {
      return String((first as { msg: unknown }).msg);
    }
    return JSON.stringify(first);
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    "Authorization": `tma ${initData()}`,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // In dev-browser mode tag every request with the developer's Telegram id
  // so the backend can identify us without HMAC.
  const dev = devUserId();
  if (dev) headers["X-Dev-User-Id"] = dev;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      throw new ApiError(0, "Сервер не отвечает — попробуйте ещё раз");
    }
    // Network failure (offline, DNS, etc.)
    throw new ApiError(0, "Нет соединения с сервером");
  } finally {
    clearTimeout(timeoutId);
  }

  if (!res.ok) {
    let detail: string | null = null;
    try {
      const json = await res.json();
      detail = describeDetail(json.detail);
    } catch {}
    throw new ApiError(res.status, detail ?? res.statusText ?? "Ошибка запроса");
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return res.json() as Promise<T>;
  return res.blob() as unknown as T;
}

// Multipart upload — used for the Автоматика CSV import. No JSON Content-Type
// (the browser sets the multipart boundary) and no short timeout (files are big).
async function uploadFile<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { "Authorization": `tma ${initData()}` };
  const dev = devUserId();
  if (dev) headers["X-Dev-User-Id"] = dev;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { method: "POST", headers, body: form });
  } catch {
    throw new ApiError(0, "Нет соединения с сервером");
  }
  if (!res.ok) {
    let detail: string | null = null;
    try { detail = describeDetail((await res.json()).detail); } catch {}
    throw new ApiError(res.status, detail ?? res.statusText ?? "Ошибка загрузки");
  }
  return res.json() as Promise<T>;
}

export const api = {
  get:    <T,>(p: string) => request<T>("GET", p),
  post:   <T,>(p: string, body?: unknown) => request<T>("POST", p, body),
  put:    <T,>(p: string, body?: unknown) => request<T>("PUT", p, body),
  patch:  <T,>(p: string, body?: unknown) => request<T>("PATCH", p, body),
  delete: <T,>(p: string) => request<T>("DELETE", p),
  upload: <T,>(p: string, form: FormData) => uploadFile<T>(p, form),
};
